export type Kind = 'org' | 'project'
export type Action = 'create' | 'read' | 'update' | 'delete' | 'join'
export type DataScope = 'own' | 'shared' | 'all'
export type ActionScopes = Partial<Record<Action, DataScope>>
export type Permissions = Record<string, ActionScopes>

export interface DomainSpec {
  key: string
  actions: Action[]
  scoped: boolean
}

export interface Role {
  role_id: number
  kind: Kind
  name: string
  description: string | null
  template_key: string | null
  is_customized: boolean
  customized_domains: string[]
  is_default_join: boolean
  permissions: Permissions
  template_permissions: Permissions | null
}

export interface AccessView {
  domains: Record<Kind, DomainSpec[]>
  roles: Role[]
  my_org_role_id: number | null
  my_project_roles: Record<string, number | null>
  my_join_grants: number[]
  is_owner: boolean
  is_super_user: boolean
  owner_member_id: number | null
}

export interface AccessRow {
  member_id?: number | null
  shared?: boolean | null
}

export const joinsSubProjects = (role: Role | undefined): boolean => !!role?.permissions?.project_children?.join

export const ACTIONS: Action[] = ['create', 'read', 'update', 'delete', 'join']
export const DATA_SCOPES: DataScope[] = ['own', 'shared', 'all']

export const SCOPE_LABELS: Record<DataScope, string> = {
  own: 'Own',
  shared: 'Own + shared',
  all: 'All',
}

const rank = (scope: DataScope) => DATA_SCOPES.indexOf(scope)

/** Mirrors apiv3 `permissions_cover`: a member grants at most what they hold, scope included (#1128). */
export function permissionsCover(caller: Permissions | undefined, granted: Permissions | undefined): boolean {
  return Object.entries(granted ?? {}).every(([domain, actions]) =>
    Object.entries(actions).every(([action, scope]) => {
      const mine = caller?.[domain]?.[action as Action]
      return !!mine && rank(mine) >= rank(scope as DataScope)
    }),
  )
}

export function canWith(
  view: AccessView | undefined,
  memberId: number | undefined,
  obj: string,
  act: Action,
  { projectId, row }: { projectId?: number | string | null; row?: AccessRow } = {},
): boolean {
  if (!view) return false
  if (view.is_owner) return true
  const roleId = projectId == null ? view.my_org_role_id : view.my_project_roles[String(projectId)]
  if (roleId == null) return false
  const scope = view.roles.find(r => r.role_id === roleId)?.permissions[obj]?.[act]
  if (!scope) return false
  if (!row || scope === 'all') return true
  const own = memberId != null && row.member_id === memberId
  return scope === 'shared' ? own || !!row.shared : own
}

interface DomainLabel {
  key: string
  label: string
  hint: string
  fullHint?: string
}

interface CatalogSection {
  label: string
  domains: DomainLabel[]
}

export interface AccessDomain extends DomainSpec {
  label: string
  hint: string
  fullHint?: string
}

export interface DomainSection {
  label: string
  domains: AccessDomain[]
}

const PROJECT_DATA: DomainLabel[] = [
  { key: 'project_feature', label: 'Features', hint: 'Points, lines and polygons' },
  { key: 'project_boundary', label: 'Boundary', hint: 'Area of interest outline' },
  { key: 'feature_resource', label: 'Files', hint: 'Attachments on features' },
  { key: 'report', label: 'Reports', hint: 'Generated reports' },
]

const CATALOG: Record<Kind, CatalogSection[]> = {
  org: [
    {
      label: 'Access',
      domains: [
        { key: 'org', label: 'Organization', hint: 'Name, avatar and plan' },
        { key: 'org_member', label: 'Members', hint: 'Who belongs to the organization' },
        { key: 'org_invite', label: 'Invites', hint: 'Pending organization invitations' },
        { key: 'org_role', label: 'Roles', hint: 'Who can do what' },
        { key: 'org_api_key', label: 'API keys', hint: 'Programmatic access tokens' },
        { key: 'org_settings', label: 'Settings', hint: 'Feature flags and defaults' },
        { key: 'project', label: 'Projects', hint: 'Create and list projects', fullHint: 'Can join any project' },
      ],
    },
    {
      label: 'Imagery',
      domains: [
        { key: 'layer_order', label: 'Imagery orders', hint: 'Purchase satellite imagery' },
        { key: 'lulc', label: 'Land cover', hint: 'Land cover maps and statistics' },
      ],
    },
    {
      label: 'Finance',
      domains: [
        { key: 'org_billing', label: 'Billing', hint: 'Subscription and payment method' },
        { key: 'bridge_wallet', label: 'Payments & wallets', hint: 'Balances and accounts' },
        { key: 'bridge_transfer', label: 'Transfers', hint: 'Send and receive funds' },
        { key: 'invoice', label: 'Invoices', hint: 'Issued and received invoices' },
      ],
    },
  ],
  project: [
    {
      label: 'Access',
      domains: [
        { key: 'project', label: 'Project', hint: 'Name, description and lifecycle' },
        { key: 'project_member', label: 'Members', hint: 'Who belongs to the project' },
        { key: 'project_invite', label: 'Invites', hint: 'Pending project invitations' },
        { key: 'project_children', label: 'Sub-projects', hint: 'Join every project inside this one' },
        { key: 'project_tag', label: 'Tags', hint: 'Project tags' },
        { key: 'report_template', label: 'Templates', hint: 'Data collection forms' },
        { key: 'project_settings', label: 'Settings', hint: 'Project configuration' },
      ],
    },
    { label: 'Data', domains: PROJECT_DATA },
    {
      label: 'Monitoring',
      domains: [
        { key: 'monitoring_settings', label: 'Monitoring settings', hint: 'What and how often to monitor' },
        { key: 'monitoring_metric', label: 'Metrics', hint: 'Time series and captures' },
        { key: 'geofence_event', label: 'Geofence events', hint: 'Entry and exit events' },
        { key: 'alert', label: 'Alerts', hint: 'Change notifications' },
      ],
    },
    {
      label: 'Collaboration',
      domains: [
        { key: 'conversation', label: 'Conversations', hint: 'Chat threads' },
        { key: 'chat_message', label: 'Chat messages', hint: 'Messages and reactions' },
      ],
    },
    {
      label: 'AI',
      domains: [
        { key: 'project_skill', label: 'Skills', hint: 'Enabled AI skills' },
        { key: 'project_agent', label: 'Agents', hint: 'Enabled AI agents' },
        { key: 'project_workflow', label: 'Workflows', hint: 'Automated pipelines' },
        { key: 'ai_review', label: 'AI review', hint: 'Run the daily AI review' },
      ],
    },
  ],
}

const warned = new Set<string>()

export function sectionsFor(kind: Kind, specs: DomainSpec[]): DomainSection[] {
  const byKey = new Map(specs.map(spec => [spec.key, spec]))
  const catalogKeys = new Set(CATALOG[kind].flatMap(s => s.domains.map(d => d.key)))
  const sections = CATALOG[kind]
    .map(section => ({
      label: section.label,
      domains: section.domains
        .filter(d => byKey.has(d.key))
        .map(d => ({ ...byKey.get(d.key)!, ...d })),
    }))
    .filter(section => section.domains.length)
  const extra = specs.filter(spec => !catalogKeys.has(spec.key))
  if (extra.length) {
    sections.push({ label: 'Other', domains: extra.map(spec => ({ ...spec, label: spec.key, hint: '' })) })
  }
  if (import.meta.env.DEV && specs.length && !warned.has(kind)) {
    warned.add(kind)
    // Full access folds `join` in with update/delete, which is only unambiguous while no
    // domain offers both.
    for (const spec of specs) {
      console.assert(
        !(spec.actions.includes('join') && (spec.actions.includes('update') || spec.actions.includes('delete'))),
        `[access] ${kind}.${spec.key} combines join with update/delete`,
      )
    }
    const missing = [...catalogKeys].filter(key => !byKey.has(key))
    if (extra.length || missing.length) {
      console.warn(`[access] ${kind} domain catalog out of sync`, {
        missingLabels: extra.map(spec => spec.key),
        unknownToServer: missing,
      })
    }
  }
  return sections
}

export type Level = 'none' | 'view' | 'edit' | 'full'

export const LEVELS: { key: Level; label: string; actions: Action[] }[] = [
  { key: 'none', label: 'No access', actions: [] },
  { key: 'view', label: 'View', actions: ['read'] },
  { key: 'edit', label: 'Edit', actions: ['create', 'read', 'update'] },
  { key: 'full', label: 'Full access', actions: ['create', 'read', 'update', 'delete', 'join'] },
]

export const levelActions = (domain: DomainSpec, level: Level): Action[] => {
  const actions = LEVELS.find(l => l.key === level)!.actions
  return domain.actions.filter(a => actions.includes(a))
}

const sameActions = (a: Action[], b: Action[]) => a.length === b.length && a.every(act => b.includes(act))

const grantedActions = (scopes: ActionScopes | undefined): Action[] =>
  ACTIONS.filter(a => scopes?.[a])

export function levelsFor(domain: DomainSpec): Level[] {
  const out: Level[] = []
  for (const { key } of LEVELS) {
    const prev = out[out.length - 1]
    if (prev && sameActions(levelActions(domain, prev), levelActions(domain, key))) {
      if (prev !== 'none') out[out.length - 1] = key
    } else {
      out.push(key)
    }
  }
  return out
}

export const levelOf = (domain: DomainSpec, scopes: ActionScopes | undefined): Level | null =>
  levelsFor(domain).find(l => sameActions(levelActions(domain, l), grantedActions(scopes))) ?? null

export const levelLabel = (domain: DomainSpec, scopes: ActionScopes | undefined): string => {
  const level = levelOf(domain, scopes)
  if (level) return LEVELS.find(l => l.key === level)!.label
  const text = grantedActions(scopes).join(', ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const seesOf = (scopes: ActionScopes | undefined): DataScope => scopes?.read ?? 'all'

export const editsOf = (scopes: ActionScopes | undefined): DataScope =>
  scopes?.update ?? scopes?.delete ?? seesOf(scopes)

export const defaultLabel = (domain: DomainSpec, scopes: ActionScopes | undefined): string => {
  const parts = [levelLabel(domain, scopes)]
  if (domain.scoped && scopes?.read) parts.push(`Sees ${SCOPE_LABELS[seesOf(scopes)]}`)
  if (domain.scoped && (scopes?.update || scopes?.delete)) parts.push(`Edits ${SCOPE_LABELS[editsOf(scopes)]}`)
  return parts.join(' · ')
}

const clamp = (scope: DataScope, max: DataScope): DataScope => (rank(scope) > rank(max) ? max : scope)

export function applyLevel(domain: DomainSpec, current: ActionScopes | undefined, level: Level): ActionScopes {
  const out: ActionScopes = {}
  const sees = seesOf(current)
  for (const act of levelActions(domain, level)) {
    if (!domain.scoped || act === 'create' || act === 'join') out[act] = 'all'
    else if (act === 'read') out[act] = sees
    else out[act] = clamp(editsOf(current), sees)
  }
  return out
}

export function applySees(current: ActionScopes, scope: DataScope): ActionScopes {
  const out: ActionScopes = { ...current, read: scope }
  for (const act of ['update', 'delete'] as const) {
    if (out[act]) out[act] = clamp(out[act]!, scope)
  }
  return out
}

export function applyEdits(current: ActionScopes, scope: DataScope): ActionScopes {
  const out: ActionScopes = { ...current }
  const edit = clamp(scope, seesOf(current))
  for (const act of ['update', 'delete'] as const) {
    if (out[act]) out[act] = edit
  }
  return out
}

export function normalizePermissions(domains: DomainSpec[], permissions: Permissions): Permissions {
  const out: Permissions = {}
  for (const domain of domains) {
    const scopes: ActionScopes = {}
    for (const act of domain.actions) {
      const scope = permissions[domain.key]?.[act]
      if (scope) scopes[act] = domain.scoped ? scope : 'all'
    }
    out[domain.key] = scopes
  }
  return out
}

export const samePermissions = (a: ActionScopes | undefined, b: ActionScopes | undefined): boolean =>
  ACTIONS.every(act => (a?.[act] ?? null) === (b?.[act] ?? null))
